import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';

import { errorMessage } from '../services/apiError';
import { artifactShareService, type ArtifactShareInfo } from '../services/artifactShareService';
import {
  ARTIFACT_SHARE_LABELS, type ArtifactShareRequest, isShareableArtifactType, OPEN_ARTIFACT_SHARE_EVENT,
} from '../utils/artifactSharing';

import { ModalBackdrop } from './ModalBackdrop';
import { ModalHeader } from './ModalHeader';

import './ShareModal.css';
import './ArtifactShareModal.css';

const focusable = 'button:not(:disabled), input:not(:disabled), select:not(:disabled), a[href]';

function useShareDialogFocus(ref: React.RefObject<HTMLDivElement | null>, onClose: () => void): void {
  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    const focusFirst = () => ref.current?.querySelector<HTMLElement>(focusable)?.focus();
    // eslint-disable-next-line max-statements
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopImmediatePropagation();
        onClose();
      } else if (event.key === 'Tab') {
        const elements = Array.from(ref.current?.querySelectorAll<HTMLElement>(focusable) ?? []);
        const next = event.shiftKey ? elements.at(-1) : elements[0];
        const boundary = event.shiftKey ? elements[0] : elements.at(-1);

        if (document.activeElement === boundary || !ref.current?.contains(document.activeElement)) {
          event.preventDefault();
          next?.focus();
        }
      }
    };

    focusFirst();
    document.addEventListener('keydown', onKey, true);

    return () => {
      document.removeEventListener('keydown', onKey, true);
      if (previous?.isConnected) previous.focus();
    };
  }, [onClose, ref]);
}

function useArtifactShare(request: ArtifactShareRequest) {
  const [share, setShare] = useState<ArtifactShareInfo | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let active = true;

    void artifactShareService.createOrGetShare(request).then((value) => {
      if (active) setShare(value);
    }).catch((failure: unknown) => {
      if (active) setError(errorMessage(failure, 'Could not create the share link. Please try again.'));
    }).finally(() => { if (active) setLoading(false); });

    return () => { active = false; };
  }, [request, attempt]);

  return { share, setShare, error, setError, loading, retry: () => {
    setLoading(true);
    setError('');
    setAttempt((value) => value + 1);
  } };
}

function ArtifactShareControls({ share, url, busy, copied, onCopy, onVisibility, onDelete }: {
  share: ArtifactShareInfo; url: string; busy: boolean; copied: boolean;
  onCopy: () => void; onVisibility: () => void; onDelete: () => void;
}) {
  return (
    <div className="share-modal__body">
      <div className="share-modal__link-row">
        <input className="share-modal__link-input" aria-label="Artifact share link" value={url}
          readOnly onClick={(event) => event.currentTarget.select()} />
        <button className="share-modal__copy-btn" type="button" onClick={onCopy} disabled={busy}>
          <i className={`fa-solid ${copied ? 'fa-check' : 'fa-copy'}`} aria-hidden="true" />
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <div className="share-modal__visibility">
        <div className="share-modal__vis-info">
          <span className="share-modal__vis-label">{share.is_public ? 'Public link' : 'Private link'}</span>
          <span className="share-modal__vis-hint">{share.is_public
            ? 'Anyone with this link can view this artifact.' : 'Only project members can view this artifact.'}</span>
        </div>
        <label className="share-modal__toggle">
          <input type="checkbox" className="share-modal__toggle-input" aria-label="Make artifact link public"
            checked={share.is_public} disabled={busy} onChange={onVisibility} />
          <span className="share-modal__toggle-slider" />
        </label>
      </div>
      <div className="artifact-share-modal__footer">
        <a className="share-modal__copy-btn" href={url} target="_blank" rel="noopener noreferrer">Open link</a>
        <button className="share-modal__delete-btn" type="button" disabled={busy} onClick={onDelete}>
          <i className="fa-solid fa-trash" aria-hidden="true" /> Delete link
        </button>
      </div>
    </div>
  );
}

// eslint-disable-next-line max-statements
export function ArtifactShareModal({ request, onClose }: { request: ArtifactShareRequest; onClose: () => void }) {
  const ref = useRef<HTMLDivElement>(null);
  const { share, setShare, loading, error, setError, retry } = useArtifactShare(request);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const title = `Share ${ARTIFACT_SHARE_LABELS[request.artifactType]}`;
  const url = share ? `${window.location.origin}/shared/artifacts/${share.share_id}` : '';

  useShareDialogFocus(ref, onClose);
  const perform = async (
    action: () => Promise<void>, fallback = 'Could not update this share link. Please try again.',
  ) => {
    setBusy(true);
    setError('');
    try { await action(); }
    catch (failure) { setError(errorMessage(failure, fallback)); }
    finally { setBusy(false); }
  };

  return createPortal(
    <ModalBackdrop isVisible onClick={onClose} className="artifact-share-modal-overlay">
      <div ref={ref} role="dialog" aria-modal="true" aria-label={title}
        className="auth-modal-content share-modal artifact-share-modal" onClick={(event) => event.stopPropagation()}>
        <ModalHeader title={title} onClose={onClose} />
        <p className="share-modal__vis-hint">This link shows the latest saved version of this artifact.</p>
        {loading ? <div className="share-modal__loading" role="status">Generating link…</div> : share ? (
          <ArtifactShareControls share={share} url={url} busy={busy} copied={copied}
            onCopy={() => void perform(async () => {
              if (!navigator.clipboard) throw new Error('Select the link and copy it manually; clipboard access is unavailable.');
              await navigator.clipboard.writeText(url);
              setCopied(true);
            }, 'Copy failed. Select the link and copy it manually.')}
            onVisibility={() => void perform(async () => {
              setShare(await artifactShareService.toggleVisibility(share, !share.is_public));
            })}
            onDelete={() => void perform(async () => {
              await artifactShareService.deleteShare(share);
              onClose();
            })} />
        ) : <button className="share-modal__copy-btn" type="button" onClick={retry}>Try again</button>}
        {error && <p className="share-modal__error" role="alert">{error}</p>}
      </div>
    </ModalBackdrop>, document.body,
  );
}

export function ArtifactShareHost() {
  const [selection, setSelection] = useState<{ request: ArtifactShareRequest; pathname: string } | null>(null);
  const { pathname } = useLocation();
  const request = selection?.pathname === pathname ? selection.request : null;
  const close = useCallback(() => setSelection((current) => (
    current?.request === request ? null : current
  )), [request]);

  useEffect(() => {
    const open = (event: Event) => {
      const value = (event as globalThis.CustomEvent<ArtifactShareRequest>).detail;

      if (value && typeof value.projectId === 'string' && isShareableArtifactType(value.artifactType)) {
        setSelection({ request: value, pathname });
      }
    };

    window.addEventListener(OPEN_ARTIFACT_SHARE_EVENT, open);

    return () => window.removeEventListener(OPEN_ARTIFACT_SHARE_EVENT, open);
  }, [pathname]);

  if (selection && selection.pathname !== pathname) setSelection(null);

  return request ? <ArtifactShareModal key={JSON.stringify(request)} request={request} onClose={close} /> : null;
}
